import { memoize } from 'lodash';

/*
  Different types on cards available on dashboard
*/

export const TYPES = {
  IMPACTS: 'Impacts',
  DONATIONS: 'Donations',
  REPORTING: 'Reporting Compliance',
  DATA: 'Data Quality',
  BENEFICIARIES: 'Beneficiaries'
};

/*
  Common metrics ussed when building cube queries
*/
export const METRICS = {
  REPORT: 'Report Count',
  BENEFICIARIES: 'Beneficiaries Reached',
  ALLOCATED: '$ Allocated'
};

/*
  Used to map the metric field related to the metric (string/key)
  selected on the impact card settings
*/
export const IMPACT_METRICS_FIELD = {
  'Total claim': 'Claims.totalClaim',
  'Total claim Catalized': 'Claims.totalLeveragedClaim',
  'SROI': 'Claims.SROI',
  'Leverage': 'Claims.leveragePercentage',
};

/*
  Default dimension used acording to set segment on card settings.
*/
export const SEGMENTS = {
  NPO: {
    dimensions: ['Reports.investmentGranteeName', 'Portfolios.id'],
    rowName: 'Reports.investmentGranteeName'
  },
  Portfolio: {
    dimensions: ['Portfolios.name', 'Portfolios.id'],
    rowName: 'Portfolios.name'
  },
  Manager: {
    dimensions: ['Portfolios.manager', 'Portfolios.id'],
    rowName: 'Portfolios.manager'
  },
  'Service Collection': {
    dimensions: ['Services.name', 'Portfolios.id'],
    rowName: 'Services.name'
  },
  'Year': {
    dimensions: ['Portfolios.year', 'Portfolios.id'],
    rowName: 'Portfolios.year'
  },
  None: {
    dimensions: ['Reports.name', 'Portfolios.id'],
    rowName: 'Reports.name'
  }
};

/*
  Base card configuration, used to build the base query,
  relative to the type of card and metric selected.
*/
export const CONFIG = {
  Impacts: {
    'Total claim': {
      measures: ['Claims.totalClaim'],
      valueRow: '',
      name: '',
      dimensions: ['Indicators.name'],
      filters: [{'dimension':'Portfolios.status','operator':'equals','values':['Active']}]
    },
    'Total claim Catalized': {
      measures: ['Claims.totalLeveragedClaim'],
      valueRow: '',
      name: '',
      dimensions: ['Indicators.name'],
      filters: [{'dimension':'Portfolios.status','operator':'equals','values':['Active']}]
    },
    'SROI': {
      measures: ['Claims.SROI'],
      valueRow: '',
      name: '',
      dimensions: ['Indicators.name'],
      filters: [{'dimension':'Portfolios.status','operator':'equals','values':['Active']}]
    },
    'Leverage': {
      measures: ['Claims.leveragePercentage'],
      valueRow: '',
      name: '',
      dimensions: ['Indicators.name'],
      filters: [{'dimension':'Portfolios.status','operator':'equals','values':['Active']}]
    }
  },
  Donations: {
    Generic: {
      measures: [
        'Investments.cashAmount',
        'Investments.goodsAmount',
        'Investments.volunteerismAmount',
        'Investments.servicesAmount',
        'Investments.totalAmount'
      ],
      valueRow: null,
      name: null,
      dimensions: ['Portfolios.year'],
      filters: []
    }
  },
  'Reporting Compliance': {
    Generic: {
      measures: ['Reports.count'],
      valueRow: null,
      name: null,
      dimensions: ['Reports.status', 'Portfolios.year'],
      filters: []
    }
  },
  'Data Quality': {
    Generic: {
      measures: ['Reports.count'],
      valueRow: '',
      name: '',
      dimensions: ['Reports.nonGuess', 'Portfolios.year'],
      filters: []
    },
  },
  Beneficiaries: {
    'Report Count': {
      measures: ['Reports.count'],
      valueRow: 'Reports.count',
      name: 'Total: [TOTAL] programs supported in [YEARS] that serve [TAGS]',
      dimensions: ['BenficiaryDemographics.demographicId'],
      filters: []
    },
    'Beneficiaries Reached': {
      measures: ['Reports.count'],
      valueRow: 'Results.forecastCurrent',
      name: 'Total: [TOTAL] beneficiaries reached in [YEARS] that serve [TAGS]',
      dimensions: [
        'BenficiaryDemographics.demographicId',
        'Results.forecastCurrent'
      ],
      filters: []
    },
    '$ Allocated': {
      measures: ['Reports.allocated'],
      valueRow: 'Reports.allocated',
      name:
        'Total: $[TOTAL] in donations in [YEARS] allocated to programs that serve [TAGS]',
      dimensions: ['BenficiaryDemographics.demographicId'],
      filters: []
    }
  }
};

/*
  Settings filter list of values used to fill
  a selectec input.
*/
export const INVITATION_BY_DAYS = {
  '15 Days': '15',
  '30 Days': '30',
  '2 months': '60',
  '3 months': '91',
  '6 months': '182',
  '1 year': '365'
};

/*
  Settings filter list of values used to fill
  a selectec input.
*/
const getAllocationRangeFilter = memoize((range) => {
  switch (range) {
    case '$1000 or less':
      return {
        LTE: {dimension: 'Claims.amount', operator: 'lte', values: ['1000']}
      };
    case '$1000 - $10K':
      return {
        GTE: {dimension: 'Claims.amount', operator: 'gte', values: ['1000']},
        LTE: {dimension: 'Claims.amount', operator: 'lte', values: ['10000']}
      };
    case '$10K - $100K':
      return {
        GTE: {dimension: 'Claims.amount', operator: 'gte', values: ['10000']},
        LTE: {dimension: 'Claims.amount', operator: 'lte', values: ['100000']}
      };
    case '$100K - $1M':
      return {
        GTE: {dimension: 'Claims.amount', operator: 'gte', values: ['100000']},
        LTE: {dimension: 'Claims.amount', operator: 'lte', values: ['1000000']}
      };
    case '$1M - $10M':
      return {
        GTE: {dimension: 'Claims.amount', operator: 'gte', values: ['1000000']},
        LTE: {dimension: 'Claims.amount', operator: 'lte', values: ['10000000']}
      };
    case '$10M - $100M':
      return {
        GTE: {dimension: 'Claims.amount', operator: 'gte', values: ['10000000']},
        LTE: {dimension: 'Claims.amount', operator: 'lte', values: ['100000000']}
      };
    case '$100M or more':
      return {
        GTE: {dimension: 'Claims.amount', operator: 'gte', values: ['100000000']},
      };
    default:
      return; 
  }
});

/*
  Builds query custom/extra filters according to filter
  set on the card settings on dashboard.
*/
export const getFilters = memoize((settings, cardType) => {
  const currentYear = new Date().getFullYear();
  const filters = [];
  const years = [];

  // eslint-disable-next-line no-restricted-globals
  if (isNaN(settings.filter)) {
    settings.filter.split(',').forEach(item => {
      switch (item) {
        case 'This Year':
          years.push(`${currentYear}`);
          break;
        case 'All time':
          break;
        case '':
          break;
        default:
          years.push(item);
      }
    });
  } else {
    years.push(`${settings.filter}`);
  }

  if (
    settings.comparisonyear &&
    settings.comparisonyear !== 'None' &&
    settings.comparisonyear !== 'No Comparison'
  ) {
    years.push(`${settings.comparisonyear}`);
  }

  if (
    years &&
    years.length > 0 &&
    settings.compare &&
    settings.compare === 'Last Year'
  ) {
    years.push(`${parseInt(years[0] - 1, 10)}`);
  }

  if (years && years.length > 0) {
    filters.push({
      dimension: 'Portfolios.year',
      operator: 'equals',
      values: years
    });
  }

  if (settings.status) {
    const statusList = [];
    // eslint-disable-next-line no-constant-condition
    if (settings.status === 'Active' || settings.status === 'All') statusList.push('Active');
    if (settings.status === 'Archived' || settings.status === 'All') statusList.push('Archived');
    if (cardType !== 'Donations') {
      filters.push({
        dimension: 'Reports.portfolioStatus',
        operator: 'equals',
        values: statusList
      });
    }
    /*filters.push({
      dimension: (cardType === "Donations") ? 'Portfolios.status' : 'Reports.portfolioStatus',
      operator: 'equals',
      values: statusList
    });*/
  }

  if (settings.invitationAgo && settings.invitationAgo !== 'No limit') {
    filters.push({
      dimension: 'Reports.invitationAgo',
      operator: 'lte',
      values: [INVITATION_BY_DAYS[settings.invitationAgo]]
    });
  }

  if (settings.indicators) {
    filters.push({
      dimension: 'Indicators.name',
      operator: 'equals',
      values: settings.indicators,
    });
  }

  if (settings.portfolios && settings.portfolios.length > 0 ) {
    filters.push({
      dimension: 'Portfolios.name',
      operator: 'equals',
      values: settings.portfolios,
    });
  }

  if (settings.services && settings.services.length > 0 ) {
    filters.push({
      dimension: 'Services.name',
      operator: 'equals',
      values: settings.services,
    });
  }

  if (settings.allocation && settings.allocation !== 'All') {
    const { GTE, LTE } = getAllocationRangeFilter(settings.allocation);
    if (GTE) filters.push(GTE);
    if (LTE) filters.push(LTE);
  }

  return filters;
});

/*
  Settings filter list of values used to fill
  a selectec input.
*/
export const DONATION_TYPES = {
  Cash: 'Investments.cashAmount',
  Goods: 'Investments.goodsAmount',
  Services: 'Investments.servicesAmount',
  Volunteerism: 'Investments.volunteerismAmount',
  'All types': '',
};

/*
  Builds query custom/extra filters according to filter
  set on the card settings on dashboard.
*/
export const getFilterYear = filter => {
  // eslint-disable-next-line no-restricted-globals
  if (isNaN(filter)) {
    switch (filter) {
      case 'This Year':
        return { filterYear: new Date().getFullYear(), title: 'This Year' };
      case '':
        return { filterYear: null, title: 'All Years' };
      default:
        return { filterYear: null, title: filter };
    }
  }
  return { filterYear: parseInt(filter, 10), title: filter };
};

/*
  Settings filter list of values used to fill
  a selectec input.
*/
export const DONATION_COMPARE = {
  NONE: '',
  NO_COMPARISON: 'No Comparison',
  LAST_YEAR: 'Last Year'
};

/*
  Settings filter list of values used to fill
  a selectec input.
*/
export const SEGMENT_FILTER_FIELD = {
  NPO: 'npo',
  Portfolio: 'portfolio',
  Manager: 'manager',
  'Service Collection': 'service',
  None: 'report'
};


/*
  Return helps text for specified field
*/
export const getHelpText = field => {
  switch (field) {
    case 'Total claim':
      return 'Total Claimed is the sum of the claimed impacts, IE how many people housed, how many dollars saved, etc';
    case 'SROI':
      return 'SROI is short for Social Return on Investment, and is calculated as the total claimed impact per $10k of value invested';
    case 'Leverage':
      return 'Leverage is a percentage measure of how much additional social impact is created due to a foundational investment, ' +
        'without which the whole program would not have existed or succeeded';
    case 'Portfolio':
      return 'Portfolio is a collection of investments and the related impact reports';
    case 'Manager':
      return 'Portfolio manager is a True Impact user in this account who either created or is responsible for a given portfolio.  ' +
        'Portfolio managers may also be responsible for individual donations in other portfolios.';
    case 'Year':
      return 'Segments the data by portfolio year';
    case 'Allocation amount':
      return 'Allocated amount is the dollar value allocated to the given program by the nonprofit. This may be the entire donation ' +
        'amount, or they may split the donation across multiple programs or years.';
    case 'Include Catalyzed Claim':
      return 'Catalyzed claim is the claimed impacts created by a foundational investment above and beyond those directly funded, ' +
        'without which the whole program would not have existed or succeeded';
    case 'This Year':
      return 'Show only data for this portfolio year';
    case 'All time':
      return 'Show all years data';
    case 'Other Year':
      return 'Show only data for another portfolio year';
    case 'Selected Service':
      return 'This is the service model selected by the nonprofit as the collection of impacts that closest fits their program';
    case 'Add Indicator':
      return 'Indicators are specific types of impacts with a specific definition for success, for example: Number of people housed';
    case 'General':
      return 'Hover a field to get more information about its functionality';
    default:
      return field;
  }
};
